import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalStateContext } from '@context/GlobalContextProvider';
import {
  CircularProgress,
  Backdrop,
  Box,
  Tooltip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { gUtils } from '@/utils/gUtils';
import { biz3utils } from '@/utils/biz3utils';
import { Buffer } from 'buffer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MobileDeviceHistory from './MobileDeviceHistory';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import BlurOverlay from './BlurOverlay';
import { isAppHome } from '@/services/deviceService';
import { useTranslation } from 'react-i18next';

export default function DeviceHistory({ deviceUUID: propDeviceUUID, showToolBar = false, fullHeight = true }) {
  const { gManageGroup, gStripe } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const [deviceHistory, setDeviceHistory] = useState([]);
  const [timestamp, setTimestamp] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const loadingTimer = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchParams] = useSearchParams();
  const searchDeviceUUID = searchParams.get('deviceUUID');
  const keyLevel = searchParams.get('keyLevel');
  const deviceUUID = propDeviceUUID || searchDeviceUUID;
  const noToolBar = gStripe.isFromApp || !showToolBar;
  // 旧原生 APP 的 WebView 外壳自带加载转圈，不再叠加
  const showLoading = isLoading && !(gStripe.isFromApp && !isAppHome);
  const [menuState, setMenuState] = useState({ open: false, item: null });
  const { t } = useTranslation();

  const loadHistory = (lastKey = null, cb) => {
    // 只在首屏加载时居中转圈，加载更多沿用列表顶部的触发
    if (!lastKey) {
      setIsLoading(true);
      // 无网络时请求不会返回，15 秒后停止转圈
      clearTimeout(loadingTimer.current);
      loadingTimer.current = setTimeout(() => setIsLoading(false), 15000);
    }
    gManageGroup.getDeviceHistory([{ deviceUUID, lastKey }], (resp) => {
      if (!lastKey) {
        clearTimeout(loadingTimer.current);
        setIsLoading(false);
      }
      const res = resp.data || [];
      setDeviceHistory((prev) => [...prev, ...res]);
      if (res.length > 0) {
        const newLastKey = res[res.length - 1]?.timestamp;
        setTimestamp(newLastKey);
        cb && cb(newLastKey);
      } else {
        cb && cb(null);
      }
    });
  };

  const downloadDeviceHistory = async (isCsv) => {
    if (deviceHistory.length < 1 || isDownloading) {
      return;
    }
    let allHistory = [];
    setIsDownloading(true);
    const pageSize = 100;
    const fetchAllHistory = async (currentLastKey = null) => {
      return new Promise((resolve) => {
        gManageGroup.getDeviceHistory(
          [{ deviceUUID, lastKey: currentLastKey }],
          (resp) => {
            const res = resp.data || [];
            allHistory = [...allHistory, ...res];
            if (res.length > 0 && res.length === pageSize) {
              const newLastKey = res[res.length - 1]?.timestamp;
              setTimeout(() => {
                fetchAllHistory(newLastKey).then(resolve);
              }, 100);
            } else {
              setIsDownloading(false);
              resolve();
            }
          },
          pageSize
        );
      });
    };
    await fetchAllHistory();
    if (allHistory.length > 0) {
      gUtils.csvUtils.downloadLists(allHistory, isCsv);
    }
  };

  useEffect(() => {
    if (deviceUUID) {
      loadHistory(null);
    }
  }, [deviceUUID]);

  useEffect(() => () => clearTimeout(loadingTimer.current), []);

  const disableInteraction = useMemo(() => {
    return parseInt(keyLevel) === 2;
  }, [keyLevel]);

  const handleItemLongPress = (item, event) => {
    event.preventDefault();
    setMenuState({ open: true, item });
  };

  const handleCloseMenu = () => {
    setMenuState({ open: false, item: null });
  };

  const handleItemClick = useCallback(
    (item) => {
      gManageGroup.getHistoryEnv({ deviceUUID: item.device_id, timestamp: item.timestamp }, (resp) => {
        const raw = resp?.data ?? {};
        const json = typeof raw === 'string' ? raw : JSON.stringify(raw);
        biz3utils.openEnvSnapshot(Buffer.from(json, 'utf8').toString('base64'), gStripe.isFromApp, navigate);
      });
    },
    [gManageGroup, gStripe.isFromApp, navigate]
  );

  const handleItemDetails = useCallback(() => {
    if (!menuState.item) {
      handleCloseMenu();
      return;
    }
    const { device_id: deviceUUID, timestamp } = menuState.item;
    gManageGroup.makeInvisibleHistory({ deviceUUID, timestamp }, (res) => {
      res.success && setDeviceHistory((prev) => prev.filter((item) => item.timestamp !== menuState.item.timestamp));
    });
    handleCloseMenu();
  }, [menuState]);

  const content = noToolBar ? (
    <MobileDeviceHistory
      fullHeight={fullHeight}
      histories={deviceHistory}
      loading={showLoading}
      onLoadMore={(cb) => {
        loadHistory(timestamp, (lastKey) => {
          cb(lastKey);
        });
      }}
      onItemLongPress={handleItemLongPress}
      onItemClick={handleItemClick}
    />
  ) : (
    <>
      <Box>
        <Tooltip title="CSVダウンロード">
          <IconButton onClick={() => downloadDeviceHistory(true)}>
            <CloudDownloadIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excelダウンロード">
          <IconButton onClick={() => downloadDeviceHistory(false)}>
            <SimCardDownloadIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            height: '480px',
            overflow: 'auto',
            bgcolor: 'background.paper',
            mr: 0,
            mt: 1,
          }}
        >
          <MobileDeviceHistory
            fullHeight={false}
            histories={deviceHistory}
            loading={showLoading}
            onLoadMore={(cb) => {
              loadHistory(timestamp, (lastKey) => {
                cb(lastKey);
              });
            }}
            onItemLongPress={handleItemLongPress}
            onItemClick={handleItemClick}
          />
        </Box>
      </Box>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
        open={isDownloading}
      >
        <CircularProgress />
        <div>履歴データをダウンロード中...</div>
      </Backdrop>
    </>
  );

  return (
    <>
      <BlurOverlay enabled={disableInteraction} style={fullHeight ? undefined : { height: '100%' }}>
        {content}
      </BlurOverlay>
      <Drawer anchor="bottom" open={menuState.open} onClose={handleCloseMenu}>
        <List sx={{ pb: 1, justifyContent: 'center' }} disablePadding>
          <ListItem disablePadding>
            <ListItemButton onClick={handleItemDetails}>
              <ListItemText
                primary={t('pages.ir.remote.delete')}
                sx={{
                  textAlign: 'center',
                  color: 'error.main',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleCloseMenu}>
              <ListItemText primary={t('pages.ir.remote.cancel')} sx={{ textAlign: 'center' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
